import { useState, useEffect, useRef, useCallback } from 'react';

const useIconOverlay = (containerRef, annMeta, hiddenGroups) => {
  const [icons, setIcons] = useState([]);
  const annMetaRef = useRef(annMeta);
  const hiddenRef = useRef(hiddenGroups);
  const prevRef = useRef([]);

  useEffect(() => { annMetaRef.current = annMeta; }, [annMeta]);
  useEffect(() => { hiddenRef.current = hiddenGroups; }, [hiddenGroups]);

  const compute = useCallback(() => {
    const gd = containerRef.current?.querySelector('.js-plotly-plot');
    if (!gd?._fullLayout) return;

    const { xaxis, yaxis2, margin } = gd._fullLayout;
    if (!xaxis?.d2p || !yaxis2?.l2p) return;

    const result = annMetaRef.current
      .filter((ev) => ev.hasSvg && !hiddenRef.current.has(ev.legendGroup))
      .map((ev) => ({
        xPx: margin.l + xaxis.d2p(ev.xDate),
        yPx: margin.t + yaxis2.l2p(ev.markerY),
        color: ev.style.color,
        iconType: ev.iconType,
        key: `${ev.legendGroup}-${ev.t}`,
      }));

    // N'update l'état que si les positions ont réellement changé
    const prev = prevRef.current;
    const changed =
      result.length !== prev.length ||
      result.some((ic, i) => ic.xPx !== prev[i]?.xPx || ic.yPx !== prev[i]?.yPx || ic.key !== prev[i]?.key);

    if (changed) {
      prevRef.current = result;
      setIcons(result);
    }
  }, [containerRef]);

  // Positionnement initial et mise à jour quand les données ou la visibilité changent.
  // requestAnimationFrame garantit que Plotly a fini son render avant de lire _fullLayout.
  useEffect(() => {
    const raf = requestAnimationFrame(compute);
    return () => cancelAnimationFrame(raf);
  }, [annMeta, hiddenGroups, compute]);

  // plotly_relayout seulement (zoom / pan / resize) — pas plotly_afterplot qui crée une boucle.
  useEffect(() => {
    if (!containerRef.current) return;
    let gd = null;

    const attach = (el) => {
      gd = el;
      gd.on('plotly_relayout', compute);
    };

    const existing = containerRef.current.querySelector('.js-plotly-plot');
    if (existing) {
      attach(existing);
      return () => { gd.removeListener?.('plotly_relayout', compute); };
    }

    const observer = new MutationObserver(() => {
      const el = containerRef.current?.querySelector('.js-plotly-plot');
      if (el && !gd) { attach(el); observer.disconnect(); }
    });
    observer.observe(containerRef.current, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      gd?.removeListener?.('plotly_relayout', compute);
    };
  }, [containerRef, compute]);

  return icons;
};

export default useIconOverlay;
